import { Controller } from "@hotwired/stimulus"

// Observes inventory row checkboxes and updates quantity/LBS totals when selection changes
export default class extends Controller {
  static targets = ["container", "accentable", "itemsCount", "quantityTotal", "weightTotal", "costPerTonTotal", "extendedCostTotal"]

  static values = {
    /** Decimal places for quantity total; inventory uses 0; PoTrack line qty sum uses 0 for whole-number display. */
    quantityDecimals: { type: Number, default: 0 },
  }

  static selector = '[data-print-selection-target="rowCheckbox"], [data-print-selection-target="selectAll"]'

  connect() {
    this.boundUpdate = this.update.bind(this)
    document.addEventListener("change", this.boundUpdate)
    this.update()
  }

  disconnect() {
    document.removeEventListener("change", this.boundUpdate)
  }

  reset() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.add("opacity-25")
      this.containerTarget.classList.remove("opacity-100")
    }
    if (this.hasAccentableTarget) {
      this.accentableTargets.forEach((el) => {
        el.classList.add("text-base-content")
        el.classList.remove("text-accent")
      })
    }
    if (this.hasItemsCountTarget) this.itemsCountTarget.textContent = "0"
    if (this.hasQuantityTotalTarget) this.quantityTotalTarget.textContent = "0"
    if (this.hasWeightTotalTarget) this.weightTotalTarget.textContent = "0"
    if (this.hasCostPerTonTotalTarget) this.costPerTonTotalTarget.textContent = this.formatCurrency(0)
    if (this.hasExtendedCostTotalTarget) this.extendedCostTotalTarget.textContent = this.formatCurrency(0)
  }

  update(event) {
    if (event && !event.target.matches(this.constructor.selector)) return

    const checkboxes = document.querySelectorAll('[data-print-selection-target="rowCheckbox"]:checked')
    let quantityTotal = 0
    let weightTotal = 0
    let costPerTonTotal = 0
    let extendedCostTotal = 0

    checkboxes.forEach((cb) => {
      const row = cb.closest('[data-print-selection-target="row"]')
      if (row) {
        quantityTotal += parseFloat(row.dataset.quantity || 0) || 0
        weightTotal += parseFloat(row.dataset.weightLbs || 0) || 0
        costPerTonTotal += parseFloat(row.dataset.costPerTon || 0) || 0
        extendedCostTotal += parseFloat(row.dataset.extendedCost || 0) || 0
      }
    })

    const count = checkboxes.length
    const quantityFormatted = this.formatNumber(quantityTotal, this.quantityDecimalsValue)
    const weightFormatted = this.formatNumber(weightTotal, 1)
    const costPerTonAvg = count > 0 ? costPerTonTotal / count : 0
    const costPerTonFormatted = this.formatCurrency(costPerTonAvg)
    const extendedCostFormatted = this.formatCurrency(extendedCostTotal)

    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle("opacity-25", count === 0)
      this.containerTarget.classList.toggle("opacity-100", count > 0)
    }
    if (this.hasAccentableTarget) {
      this.accentableTargets.forEach((el) => {
        el.classList.toggle("text-base-content", count === 0)
        el.classList.toggle("text-accent", count > 0)
      })
    }
    if (this.hasItemsCountTarget) this.itemsCountTarget.textContent = this.formatNumber(count)
    if (this.hasQuantityTotalTarget) this.quantityTotalTarget.textContent = quantityFormatted
    if (this.hasWeightTotalTarget) this.weightTotalTarget.textContent = weightFormatted
    if (this.hasCostPerTonTotalTarget) this.costPerTonTotalTarget.textContent = costPerTonFormatted
    if (this.hasExtendedCostTotalTarget) this.extendedCostTotalTarget.textContent = extendedCostFormatted
  }

  formatCurrency(value) {
    const num = Number(value) || 0
    return num.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  formatNumber(value, decimals = 0) {
    const num = Number(value) || 0
    if (decimals > 0) {
      return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    }
    return num.toLocaleString()
  }
}
